import { deckCover, deckLadder, gameCover, gameStatus, madeCover, READING_PATH, readDeck, readEngineMaterial, rowStep, type Deck } from '@aurora/core'
import { auroraStore } from '../store/auroraStore'
import { materialKind, pathPlayed } from '../store/selectors'
import { kindLabel } from './specs'

/**
 * The few things every surface does with a game (the Playroom, an
 * activity card, the agent's reply, a game's page), in one place so they
 * cannot drift: what to call it, what picture it wears, play it, retry it.
 */

const act = () => auroraStore.getState() as any

/** What a parent calls it: a story is a book, anything else she plays is a game. */
export function gameNoun(a: any): string {
  if (a?.kind === 'deck') return 'Cards'
  const engine = typeof a?.html === 'string' ? readEngineMaterial(a.html)?.engine : undefined
  if (engine === 'story') return 'Book'
  if (engine || typeof a?.html === 'string' || gameStatus(a) !== 'ready') return 'Game'
  return kindLabel(materialKind(a))
}

/**
 * What a game wears. A reading-path step wears the cover the path gives it.
 * Otherwise a picture made for this very version of its cover thing comes
 * first (core's madeCover), since it shows what the game is now (the pink
 * jellyfish she asked for, not the bank's blue one); then core's gameCover, the same as on the phone (a
 * book's cover, else the first thing its title or request names that has a
 * clay picture or an emoji, else its theme); then the first letter of its
 * name after hers. Every game wears something of its own, so a list of
 * them reads as the games.
 */
export function gameFace(a: any, childName?: string): { picture: string | null; emoji: string | null; letter: string } {
  const { cover: own, letter } = gameCover(a ?? {}, childName)
  const step = stepOf(a)?.cover
  // Only a picture of the cover thing: a game makes pictures of everyone in it (the teddy helping in the ladybug game).
  const made = step ? undefined : madeCover({ cover: own, assets: a?.assets })
  if (made?.url) return { picture: made.url, emoji: null, letter }
  const { picture, emoji } = deckCover({ cover: step ?? own })
  return { picture, emoji, letter }
}

/** The broad areas a generated game's first tag names; the tag after it is the skill. */
const DOMAINS = new Set(['literacy', 'reading', 'math', 'maths', 'numeracy', 'language', 'science', 'music', 'art', 'motor', 'social'])

/**
 * What a made game practises, in two or three words ("Letter sounds",
 * "Counting"): the skill the generator tagged it with, after its domain.
 * Undefined when it has no skill tag.
 */
export function gameSkill(a: any): string | undefined {
  // A path step practises its track ("Blending", "Sight words"); a book's noun already says Book.
  const step = stepOf(a)
  if (step) return step.track === 'books' ? undefined : READING_PATH.find((t) => t.id === step.track)?.title
  const tags: string[] = (Array.isArray(a?.tags) ? a.tags : []).filter((t: unknown) => typeof t === 'string' && t.trim() && t !== 'path' && t !== 'deck')
  const skill = tags[0] && DOMAINS.has(tags[0].toLowerCase()) ? tags[1] : tags[0]
  return skill ? skill.charAt(0).toUpperCase() + skill.slice(1) : undefined
}

/**
 * When a game was made: the moment its maker finished, which is when the
 * parent first could play it, not when it was asked for. A row from before
 * finish times were kept falls back to when it was asked for.
 */
export function madeAt(a: any): number | undefined {
  return a?.generation?.finishedAt ?? a?.createdAt
}

/**
 * What a few things are, counted the way a parent says it: "1 book",
 * "2 games", "1 game and 1 book". A thing being made is still a game.
 */
export function countThings(list: any[]): string {
  const counts = new Map<string, number>()
  for (const a of list) {
    const noun = gameNoun(a)
    const word = noun === 'Book' ? 'book' : noun === 'Cards' ? 'deck' : 'game'
    counts.set(word, (counts.get(word) ?? 0) + 1)
  }
  return [...counts].map(([w, n]) => `${n} ${n === 1 ? w : `${w}s`}`).join(' and ')
}

/** Whether a row is her copy of a reading-path step (a built-in book or set), not something made. */
export function isPathStep(a: any): boolean {
  return !!stepOf(a)
}

/** Hand it to her. From an activity, the block rides along so finishing it finishes the activity. */
export function playGame(id: string, blockRef?: string) {
  act().enterKidMode(id, blockRef)
}

/** A game's own page; `preview` opens it playing in the page, for the parent to hold (a locked book read together). */
export function openGame(id: string, opts?: { preview?: boolean }) {
  act().openGame(id, opts)
}

/** A game that did not come back, made again in place: same row, same words, same activity. */
export function retryGame(a: any) {
  if (a?._id && a.generation?.status === 'failed') act().retryGeneration({ artifactId: a._id })
}

/** Start a game from a sentence. It is in the list at once, making. */
export function makeGame(childId: string, prompt: string, blockRef?: string) {
  act().generateMaterial({ childId, prompt, blockRef })
}

/** The reading-path step a row is her copy of, as it is for her (a book rewritten for her wears its own title and cover), if it is one. */
const stepOf = rowStep

let seq = 0
/**
 * Play a reading-path step: her own row for it when she has one, so her
 * level carries on; otherwise the row is made now and she starts at once.
 */
export function playStep(childId: string, stepId: string) {
  const s = act()
  const mine = pathPlayed(s)[stepId]?.artifactId
  if (mine) return playGame(mine)
  const clientId = `path_${stepId}_${Date.now().toString(36)}${(seq++).toString(36)}`
  s.openPathStep({ childId, step: stepId, clientId })
  playGame(clientId)
}

/**
 * What a game being made says after its seconds, one wording everywhere it
 * shows: the usual time while it is usual, and the truth once it is not.
 */
export function makingNote(seconds: number): string {
  return seconds > 240 ? 'taking longer than usual, still going' : 'usually 1 to 4 minutes'
}

/** Real elapsed time, as a parent reads a stopwatch: 42s, 1:07. */
export function clock(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  return `${m}:${String(seconds % 60).padStart(2, '0')}`
}

/** Past this many settled games, the rest are grouped by what they practise. */
const GROUP_AFTER = 6

export interface GameGroup {
  key: string
  /** Empty for a list short enough to need no heading. */
  title: string
  games: any[]
  /** Shown whole: what matters now never folds away. */
  open?: boolean
}

/** How long a game that just came back stays at the top, where the parent who asked for it is looking. */
const JUST_MADE_MS = 10 * 60_000

/**
 * Made games in the order a parent reaches for them. First what is moving
 * (being made, a change being made, did not come back) and what came back
 * in the last few minutes, so a game asked for in the composer stays where
 * it appeared until the parent has seen it; then what was made for today's
 * activities; then the rest: as they are when there are a few, else by what
 * they practise, the skill with the newest game first. `games` come newest
 * first; `changing` names the ready games with a change on its way.
 */
export function groupGames(games: any[], opts: { today: string; blocks: Record<string, any>; changing?: Set<string>; now?: number }): GameGroup[] {
  const moving: any[] = []
  const today: any[] = []
  const bySkill = new Map<string, any[]>()
  const fresh = (g: any) => opts.now !== undefined && opts.now - (g.generation?.finishedAt ?? 0) < JUST_MADE_MS
  for (const g of games) {
    if (gameStatus(g) !== 'ready' || opts.changing?.has(g._id) || fresh(g)) moving.push(g)
    else if (g.blockRef && opts.blocks[g.blockRef]?.date === opts.today) today.push(g)
    else {
      const skill = gameNoun(g) === 'Book' ? 'Books' : gameSkill(g) ?? 'Other games'
      bySkill.set(skill, [...(bySkill.get(skill) ?? []), g])
    }
  }
  const groups: GameGroup[] = []
  if (moving.length) {
    const making = moving.filter((g) => gameStatus(g) !== 'ready' || opts.changing?.has(g._id)).length
    groups.push({ key: 'now', title: making === moving.length ? 'Being made' : making ? 'Just now' : 'Just made', games: moving, open: true })
  }
  if (today.length) groups.push({ key: 'today', title: "For today's activities", games: today, open: true })
  // A short list reads at a glance as it is; headings earn their place once it is long.
  const rest = [...bySkill.values()].flat()
  if (rest.length <= GROUP_AFTER) {
    if (rest.length) groups.push({ key: 'rest', title: groups.length ? 'Earlier' : '', games: games.filter((g) => rest.includes(g)), open: true })
    return groups
  }
  // A skill with one game is not a heading worth scanning for, and alone it
  // leaves half a row empty: it joins the catch-all, which goes last whatever
  // its age, while a named skill is what a parent scans for.
  const more: any[] = []
  for (const [title, list] of [...bySkill]) {
    if (list.length < 2 || title === 'Other games') { more.push(...list); bySkill.delete(title) }
  }
  for (const [title, list] of bySkill) groups.push({ key: `skill:${title}`, title, games: list })
  if (more.length) groups.push({ key: 'skill:more', title: bySkill.size ? 'More games' : 'Earlier', games: games.filter((g) => more.includes(g)) })
  return groups
}

/**
 * Groups of one game that sit side by side on a wide screen: a lone game
 * ("Just made", "For today's activities") fills half a row, so two such
 * groups next to each other share the row, each under its own heading. A
 * single with no single beside it takes the full row instead of leaving an
 * empty column that makes the newest news look least important.
 */
export function pairSingles(groups: Array<{ games: unknown[] }>): Set<number> {
  const halves = new Set<number>()
  for (let i = 0; i + 1 < groups.length; i++) {
    if (groups[i]!.games.length === 1 && groups[i + 1]!.games.length === 1) { halves.add(i).add(i + 1); i++ }
  }
  return halves
}

/**
 * A deck in a line, as it is now (an edit to fewer a day, or resting, shows
 * at once): its size and its pace. Its size counts the cards its tile and
 * the planner count (deckDay's ladder, one per card id), so "81 cards" here
 * is the 81 in "1 of 81 known".
 */
export function deckLine(deck: Deck): string {
  const n = new Set(deckLadder(deck).map((k) => k.toLowerCase())).size
  return `${n} card${n === 1 ? '' : 's'} · ${deck.paused ? 'resting' : `${deck.daily ?? 10} a day`}`
}

/** What a made thing says under its name in a list: a deck's line from the deck now, else what was saved with it. */
export function madeSummary(a: any): string | undefined {
  const deck = a?.kind === 'deck' ? readDeck(a) : null
  return deck ? deckLine(deck) : a?.summary
}
